import { useEffect, useState } from "react";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Ticket, Trophy, Users, Building2 } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const hojeStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const inicioMesStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };
const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const inputCls = "mt-1 h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterSorteio() {
  const { unitId, unidades } = useUnit();
  const [inicio, setInicio] = useState(inicioMesStr());
  const [fim, setFim] = useState(hojeStr());
  const [quantidade, setQuantidade] = useState(1);
  const [participantes, setParticipantes] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [sorteando, setSorteando] = useState(false);

  const unitNome = unitId !== "all" ? (unidades?.find((u) => u.id === unitId)?.nome || "") : "";
  // Data digitada inválida (ex.: ano com 5 dígitos) → null, sem derrubar a tela.
  const toIso = (txt) => {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(txt)) return null;
    const d = new Date(txt);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };
  const isoIni = () => toIso(`${inicio}T00:00:00`);
  const isoFim = () => toIso(`${fim}T23:59:59.999`);
  const periodoValido = !!(isoIni() && isoFim() && inicio <= fim);

  const loadHistorico = async () => {
    try { const { data } = await masterApi.get("/master/sorteios"); setHistorico(data || []); }
    catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { loadHistorico(); }, []);

  useEffect(() => {
    setParticipantes(null);
    if (!periodoValido) return;
    const p = new URLSearchParams({ inicio: isoIni(), fim: isoFim() });
    if (unitId !== "all") p.set("unit_id", unitId);
    masterApi.get(`/master/sorteios/participantes?${p.toString()}`)
      .then((r) => setParticipantes(r.data))
      .catch((e) => modal.error(apiError(e)));
  }, [inicio, fim, unitId]);

  const sortear = async () => {
    if (!periodoValido) return modal.error("Informe um período válido (início antes do fim).");
    const qtd = Math.max(1, parseInt(quantidade) || 1);
    if (qtd > 50) return modal.error("O sorteio permite no máximo 50 ganhadores por vez.");
    const total = participantes?.total || 0;
    if (!total) return modal.error("Nenhum cliente abasteceu neste período.");
    if (qtd > total) return modal.error(`Só há ${total} participante(s) para ${qtd} ganhador(es).`);
    const ok = await modal.confirm({
      title: "Realizar sorteio",
      message: `Sortear ${qtd} ganhador${qtd > 1 ? "es" : ""} entre ${total} cliente${total > 1 ? "s" : ""} que abasteceram de ${fmtData(isoIni())} a ${fmtData(isoFim())}${unitNome ? ` na unidade ${unitNome}` : " (todas as unidades)"}? O resultado fica registrado no histórico.`,
      confirmText: "Sortear",
    });
    if (!ok) return;
    setSorteando(true);
    try {
      const { data } = await masterApi.post("/master/sorteios", {
        inicio: isoIni(), fim: isoFim(), quantidade: qtd, unit_id: unitId !== "all" ? unitId : null,
      });
      setResultado(data);
      loadHistorico();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSorteando(false); }
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-sorteio">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Gestão</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Sorteio</h1>
        <p className="text-[12px] text-neutral-500 mt-1">
          Sorteio entre os clientes que abasteceram no período. Cada cliente participa com 1 chance.
        </p>
      </div>

      <div className="card-soft rounded-xl p-4 space-y-3" data-testid="sorteio-form">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Abasteceram de</label>
            <input type="date" data-testid="sorteio-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">até</label>
            <input type="date" data-testid="sorteio-fim" value={fim} onChange={(e) => setFim(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Ganhadores</label>
            <input type="number" min={1} max={50} data-testid="sorteio-qtd" value={quantidade}
                   onChange={(e) => setQuantidade(e.target.value)} className={inputCls + " w-24"} />
          </div>
          <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-[12px] font-medium"
               style={{ background: "rgba(10,42,115,0.08)", color: "var(--brand-blue)" }} data-testid="sorteio-unidade">
            <Building2 size={14} /> {unitNome || "Todas as unidades"}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[12.5px] text-neutral-700 flex items-center gap-1.5" data-testid="sorteio-participantes">
            <Users size={14} className="text-[color:var(--brand-blue)]" />
            {!periodoValido ? "Informe um período válido."
              : participantes == null ? "Contando participantes..."
              : <span><b>{participantes.total}</b> cliente{participantes.total === 1 ? "" : "s"} participa{participantes.total === 1 ? "" : "m"} ({participantes.abastecimentos} abastecimento{participantes.abastecimentos === 1 ? "" : "s"} no período)</span>}
          </div>
          <button data-testid="sorteio-btn" onClick={sortear} disabled={sorteando || !participantes?.total}
                  className="btn-action btn-novo">
            <Ticket size={15} /> {sorteando ? "Sorteando..." : "Realizar sorteio"}
          </button>
        </div>
        <p className="text-[11px] text-neutral-500">
          A unidade segue o seletor do topo da tela. Troca de óleo não conta como abastecimento.
        </p>
      </div>

      {resultado && (
        <div className="rounded-xl p-4 border border-amber-300 shadow-sm" data-testid="sorteio-resultado"
             style={{ background: "linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 100%)" }}>
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Trophy size={17} className="text-white" />
            </div>
            <div>
              <div className="font-serif-display text-lg leading-tight text-amber-900">
                Resultado do sorteio
              </div>
              <div className="text-[11px] text-amber-800">
                {fmtDataHora(resultado.realizado_em)} · {resultado.total_participantes} participantes · período {fmtData(resultado.inicio)} a {fmtData(resultado.fim)}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {resultado.ganhadores.map((g) => (
              <div key={g.cliente_id} className="flex items-center justify-between gap-2 bg-white/80 rounded-lg px-3 py-2 border border-amber-200"
                   data-testid={`sorteio-ganhador-${g.posicao}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-[12px] font-bold flex items-center justify-center shrink-0">{g.posicao}º</span>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13px] truncate">{g.nome || "—"}</div>
                    <div className="text-[11px] text-neutral-600">{g.placa || "—"} · {formatPhone(g.telefone)}</div>
                  </div>
                </div>
                <div className="text-[11px] text-neutral-600 shrink-0">{g.abastecimentos_no_periodo} abast. no período</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="card-soft rounded-xl p-4">
        <h2 className="font-serif-display text-lg mb-2.5">Sorteios realizados</h2>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Realizado em</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Período</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Participantes</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Ganhador(es)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historico.map((s) => (
                <TableRow key={s.id} data-testid={`sorteio-hist-${s.id}`}>
                  <TableCell className="text-[12px] whitespace-nowrap">{fmtDataHora(s.realizado_em)}</TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap">{fmtData(s.inicio)} a {fmtData(s.fim)}</TableCell>
                  <TableCell className="text-[12px]">{s.unit_nome || "Todas"}</TableCell>
                  <TableCell className="text-[12px]">{s.total_participantes}</TableCell>
                  <TableCell className="text-[12px]">
                    {(s.ganhadores || []).map((g) => (
                      <div key={g.cliente_id}>{g.posicao}º {g.nome} <span className="text-neutral-500">· {g.placa}</span></div>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
              {historico.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-6">Nenhum sorteio realizado ainda.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
